import React, { useEffect, useRef, useState } from "react";
import PageLoader from "../../../shared/PageLoader";
import { useAppSelector } from "../../../../redux/hook";
import { useExportEmployees } from "../../../../backend/exports";
import { downloadFileContent } from "../../../../utils/common";
import { ERROR_MESSAGES } from "../../../../utils/constants";
import { toast } from "react-toastify";
import { getEmployees } from "../../../../redux/features/performerClient.slice";
import EmployeeUploadFilters from "../shared/EmployeeUploadFilters";
import EmployeeUploadSearch from "../../../common/EmployeeUploadSearch";
import AdditionalFiltersInputUploads from "../shared/AdditionalFiltersInputUploads";
import {
  useEmployeeUploadFilters,
  useEmployeeUploadSearchConfig,
  useEmployeeUploadData,
  usePerformerClientCompanyData,
} from "../../../../hooks/useEmployeeUploads";
import {
  formatEmployeeUploadDate,
  getEmployeeUploadCompanyNameById,
  getEmployeeUploadAssocCompanyNameById,
  getEmployeeUploadLocationById,
  buildEmployeeUploadQueryPayload,
} from "../../../../utils/employeeUploads.utils";
import { UploadButton } from "../shared";
import PerformerTable from "../shared/PerformerTable";
import "../shared/performer-client.css";
import Icon from "../../../common/Icon";


const EmployeeMasterUpload = () => {

  // Redux selectors
  const employeeDetails = useAppSelector(
    (state) => state.performerClient.employeesDetails,
  );

  // Use custom hooks
  const { filters, updateFilter, updateMultipleFilters } =
    useEmployeeUploadFilters();
  const { searchConfig, updateSearchConfig } = useEmployeeUploadSearchConfig();
  const { fetchData, fetchWithSearch } = useEmployeeUploadData({
    searchFieldName: "name_cont",
  });
  const {
    companies,
    associateCompanies,
    locations,
    fetchCompanies,
    fetchAssociateCompanies,
    fetchLocations,
  } = usePerformerClientCompanyData();

  // Local state for UI components
  const [companyName, setCompanyName] = useState("");
  const [associate_company_name, setAssociateCompanyName] = useState("");
  const [locationName, setLocationName] = useState("");
  const [companyLocationMappingId, setCompanyLocationMappingId] = useState("");
  const [showAdditionalFiltersModal, setShowAdditionalFiltersModal] =
    useState(false);
  const [selectedAdditionalFilters, setSelectedAdditionalFilters] =
    useState<any>({});

  const employees = employeeDetails.data.items || [];
  const employeesCount = employeeDetails.data.meta.total_count;

  const { exportRecords, exporting } = useExportEmployees(
    (response: any) => {
      let filename =
        associate_company_name +
        "_" +
        locationName +
        "_" +
        "Employee-Master" +
        ".xlsx";
      toast.success('Export will be sent to your registered email.')
    },
    () => {
      toast.error(ERROR_MESSAGES.DEFAULT);
    },
  );

  const loading = exporting || employeeDetails.status === "loading";

  // Data fetching effects
  useEffect(() => {
    fetchCompanies();
    fetchData(getEmployees, filters, searchConfig);
  }, []);

  // Fetch data when filters change (including additional filters)
  const isInitialRender = useRef(true);
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    updateSearchConfig({ page: 0 });
    fetchData(getEmployees, filters, { ...searchConfig, page: 0 });
  }, [
    filters.establishmentType,
    filters.company,
    filters.associateCompany,
    filters.location,
    filters.employer,
    filters.employerLocation,
    filters.subContractor,
    filters.subContractorLocation,
  ]);

  useEffect(() => {
    if (filters.company) {
      fetchAssociateCompanies(filters.company);
      const selectedCompanyName = getEmployeeUploadCompanyNameById(
        filters.company,
        companies,
      );
      setCompanyName(selectedCompanyName);
    }
  }, [filters.company, companies]);

  useEffect(() => {
    if (filters.associateCompany) {
      fetchLocations(filters.associateCompany);
      const selectedCompanyName = getEmployeeUploadAssocCompanyNameById(
        filters.associateCompany,
        associateCompanies,
      );
      setAssociateCompanyName(selectedCompanyName);
    }
  }, [filters.associateCompany, associateCompanies]);

  useEffect(() => {
    if (filters.location) {
      const selectedLocationId = filters.location.split("^")[0];
      const selectedLocationName = getEmployeeUploadLocationById(
        selectedLocationId,
        locations,
      );
      setLocationName(selectedLocationName);
      const tempLocation =
        locations.find((loc: any) => loc.location_id === selectedLocationId) ||
        {};
      setCompanyLocationMappingId(tempLocation.company_location_mapping_id || "");
    }
  }, [filters.location, locations]);

  // Clear additional filters when parent filters change
  useEffect(() => {
    setSelectedAdditionalFilters({});
  }, [
    filters.establishmentType,
    filters.company,
    filters.associateCompany,
    filters.location,
  ]);

  // Handler functions - Filter updates are handled by the hook automatically

  const handleSearch = () => {
    updateSearchConfig({ page: 0 });
    fetchData(getEmployees, filters, { ...searchConfig, page: 0 });
  };

  const handleClearSearch = () => {
    updateSearchConfig({ searchInput: "" });
    fetchData(getEmployees, filters, { ...searchConfig, searchInput: "" });
  };

  const handleSort = (sortField: string) => {
    const newSortDirection =
      searchConfig.activeSort === sortField && searchConfig.sortType === "asc"
        ? "desc"
        : "asc";
    updateSearchConfig({
      activeSort: sortField,
      sortType: newSortDirection,
    });
    fetchData(
      getEmployees,
      filters,
      searchConfig,
      {},
      sortField,
      newSortDirection,
    );
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    updateSearchConfig({ page: newPage });
    fetchData(getEmployees, filters, { ...searchConfig, page: newPage });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    updateSearchConfig({
      rowsPerPage: newRowsPerPage,
      page: 0,
    });
    fetchData(getEmployees, filters, {
      ...searchConfig,
      rowsPerPage: newRowsPerPage,
      page: 0,
    });
  };

  const handleAdditionalFiltersApply = (filterData: any) => {
    updateMultipleFilters({
      employer: filterData.payload.employer_id_eq || "",
      employerLocation: filterData.payload.employer_location_id_eq || "",
      subContractor: filterData.payload.sub_contractor_id_eq || "",
      subContractorLocation:
        filterData.payload.sub_contractor_location_id_eq || "",
    });
    setSelectedAdditionalFilters(filterData.selectedFilters || {});
    setShowAdditionalFiltersModal(false);
  };

  const onClickExport = () => {
    const { per, page, ...payload } = buildEmployeeUploadQueryPayload(
      filters,
      searchConfig,
      { searchFieldName: "name_cont" },
    );
    exportRecords(payload);
  };

  useEffect(() => {
    if (employeeDetails.status === "succeeded") {
    } else if (employeeDetails.status === "failed") {
      toast.error(ERROR_MESSAGES.DEFAULT);
    }
  }, [employeeDetails.status]);

  return (
    <>
    <div className="performerclient-wrapper h-100">
      <div className="performer-client-page h-100 py-3">
        <div style={{ height: "100%" }}>
          <div className="pageHeading mb-0" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h4 className="mb-0">Employee Master</h4>
            <EmployeeUploadSearch
              value={searchConfig.searchInput}
              placeholder="Search (Employee Name)"
              onChange={(e) => updateSearchConfig({ searchInput: e.target.value })}
              onSearch={handleSearch}
              onClear={handleClearSearch}
            />
          </div>
          <div className="filters" style={{ padding: "16px 20px" }}>
            <div style={{ backgroundColor: "#ffffff", padding: "16px 16px", borderRadius: "8px" }}>
              <EmployeeUploadFilters
                filters={filters}
                companies={companies}
                associateCompanies={associateCompanies}
                locations={locations}
                onUpdateFilter={updateFilter}
                showYearMonth={false}
                showAdditionalFilters={true}
                onAdditionalFiltersClick={() => {
                  if (
                    filters.company &&
                    filters.associateCompany &&
                    filters.location
                  ) {
                    setShowAdditionalFiltersModal(true);
                  } else {
                    toast.error(
                      "Please select Company, Associate Company and Location to apply additional filters",
                    );
                  }
                }}
                actions={
                  <>
                    <div style={{ cursor: (!employees || !filters.company || !filters.associateCompany) ? "not-allowed" : "pointer" }}>
                      <button
                        className="download-btn"
                        onClick={onClickExport}
                        disabled={
                          !employees ||
                          !filters.company ||
                          !filters.associateCompany
                        }
                        title='Export Data'
                      >
                        <Icon name="downloadIcon" />
                      </button>
                    </div>
                    <UploadButton
                      configType="Employee"
                      filters={{
                        company: filters.company,
                        associateCompany: filters.associateCompany,
                        establishmentType: filters.establishmentType,
                        location: filters.location ? filters.location.split("^")[0] : "",
                      }}
                      selectedAdditionalFilters={selectedAdditionalFilters}
                      onUploadSuccess={() =>
                        fetchData(getEmployees, filters, searchConfig)
                      }
                    />
                  </>
                }
              />
            </div>
          </div>

          {/* Additional Filters Modal */}
          {showAdditionalFiltersModal && (
            <AdditionalFiltersInputUploads
              data={{
                establishmentType: filters.establishmentType,
                company: filters.company,
                associate_company: filters.associateCompany,
                location: companyLocationMappingId,
              }}
              selectedFilters={selectedAdditionalFilters}
              onSubmit={handleAdditionalFiltersApply}
              onCancel={() => setShowAdditionalFiltersModal(false)}
            />
          )}

          <div style={{ padding: "0 20px" }}>
            <PerformerTable
              columns={[
                { label: "Employee Code", sortKey: "code", render: (row) => row.code },
                ...(filters.establishmentType === "CLRA" ? [{ label: "Company Employer Sub-Contractor Code", render: (row: any) => (row.company_employer_sub_contractor || {}).company_employer_sub_contractor_code }] : []),
                { label: "Name", sortKey: "name", render: (row) => row.name },
                { label: "Department", render: (row) => row.department },
                { label: "Designation", sortKey: "designation", render: (row) => row.designation },
                { label: "DOB", sortKey: "date_of_birth", render: (row) => formatEmployeeUploadDate(row.date_of_birth) },
                { label: "Gender", sortKey: "gender", render: (row) => row.gender },
                { label: "DOJ", sortKey: "date_of_joining", render: (row) => formatEmployeeUploadDate(row.date_of_joining) },
                { label: "Date of Permanent", render: (row) => formatEmployeeUploadDate(row.date_of_permanent) },
                { label: "Date of Leave", render: (row) => formatEmployeeUploadDate(row.date_of_leave) },
                { label: "Reason of Exit", render: (row) => row.reason_of_exit },
                { label: "Age", render: (row) => row.age },
                { label: "Father Name", render: (row) => row.father_name },
                { label: "Husband Name", render: (row) => row.husband_name },
                { label: "Identification marks", render: (row) => row.identification_marks },
                { label: "Permanent address", render: (row) => row.permanent_address },
                { label: "Temporary address", render: (row) => row.temporary_address },
                { label: "Tenure of employment", render: (row) => row.tenure_of_employment },
                { label: "BankName", render: (row) => row.bank_name },
                { label: "Vendor Nature Of Work", render: (row) => row.vendor_nature_of_work },
                { label: "Establishment Type", render: (row) => row.establishment_type },
              ]}
              data={employees}
              count={employeesCount}
              page={searchConfig.page}
              rowsPerPage={searchConfig.rowsPerPage}
              activeSort={searchConfig.activeSort}
              sortType={searchConfig.sortType}
              onSort={handleSort}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </div>
        </div>
      </div>
    </div>
    {loading && <PageLoader>Loading...</PageLoader>}
    </>
  );
};

export default EmployeeMasterUpload;
